import * as React from 'react';
import _ from 'underscore';

import { requestPost } from '../../../../modules/request/index';
import { ConceptResultList, TextEditor } from '../../index';

interface Response {
  id: string;
  text: string;
}

interface MassEditActions {
  clearDisplayMessage: () => any;
  clearResponses: () => any;
  deleteAll: (selectedResponses: string[], questionID: string) => any;
  removeResponse: (responseKey: string) => any;
  submitConceptResults: (selectedResponses: string[], conceptResults: {[key: string]: boolean} | undefined, questionID: string) => any;
  submitFeedback: (selectedResponses: string[], payload: any, questionID: string) => any;
}

interface MassEditContainerProps {
  actions: MassEditActions;
  conceptResults?: {[key: string]: boolean};
  dispatch: Function;
  displayError?: string;
  displayMessage?: string;
  massEditFeedback?: string;
  massEditOptimal?: boolean;
  questionID: string;
  selectedMassEditBoilerplate?: string;
  selectedResponses: string[];
}

interface MassEditContainerState {
  conceptResults?: {[key: string]: boolean};
  massEditFeedback?: string;
  massEditSummaryListButtonText: string;
  massEditSummaryListDisplay: string;
  newMassEditConceptResultConceptUID: string;
  newMassEditConceptResultCorrect: boolean;
  responses: {[key: string]: Response};
  selectedMassEditBoilerplate?: string;
  selectedMassEditBoilerplateCategory: string;
}

class MassEditContainer extends React.Component<MassEditContainerProps, MassEditContainerState> {
  state = {
    conceptResults: undefined,
    massEditFeedback: '',
    massEditSummaryListButtonText: 'Expand List',
    massEditSummaryListDisplay: 'none',
    newMassEditConceptResultConceptUID: '',
    newMassEditConceptResultCorrect: false,
    responses: {},
    selectedMassEditBoilerplate: '',
    selectedMassEditBoilerplateCategory: '',
  };

  componentDidMount() {
    this.getResponses();
  }

  componentDidUpdate(prevProps: MassEditContainerProps) {
    if (this.props.displayMessage !== prevProps.displayMessage || this.props.displayError !== prevProps.displayError) {
      this.displayMessage();
    }
  }

  massEditOptimalRef = React.createRef<HTMLInputElement>();

  getResponses = () => {
    const { selectedResponses } = this.props;
    requestPost(
      `${window.postbuildEnv.QUILL_CMS}/responses/mass_edit/show_many`,
      { responses: selectedResponses },
      (data) => {
        const parsedResponses = _.indexBy(data.responses, 'id');
        this.setState({
          responses: parsedResponses,
        });
      }
    );
  }

  clearResponsesFromMassEditArray = () => {
    const { actions, dispatch } = this.props;
    dispatch(actions.clearResponses());
    this.goBackToResponses();
  }

  deleteAllResponsesInMassEditArray = () => {
    const { actions, dispatch, questionID, selectedResponses } = this.props;

    if (window.confirm(`⚠️ Delete ${selectedResponses.length} responses?! 😱`)) {
      dispatch(actions.deleteAll(selectedResponses, questionID));
      this.clearResponsesFromMassEditArray();
    }
  }

  displayMessage = () => {
    const { actions, dispatch, displayError, displayMessage } = this.props;
    if (displayMessage || displayError) {
      const alert = displayMessage || displayError;
      window.alert(`${alert}`);
      dispatch(actions.clearDisplayMessage());
    }
  }

  goBackToResponses = () => {
    const newLocation = window.location.hash.replace('mass-edit', 'responses');
    window.location.href = newLocation;
  }

  handleMassEditFeedbackTextChange = (value: string) => {
    this.setState({ massEditFeedback: value });
  }

  removeResponseFromMassEditArray = (responseKey: string) => {
    const { actions, dispatch } = this.props;
    dispatch(actions.removeResponse(responseKey));
  }

  toggleMassEditSummaryList = () => {
    let display = 'none';
    let text = 'Expand List';
    if (this.state.massEditSummaryListButtonText === 'Expand List') {
      display = 'block';
      text = 'Collapse List';
    }
    this.setState({
      massEditSummaryListDisplay: display,
      massEditSummaryListButtonText: text,
    });
  }

  updateConceptResults = (conceptResults: {[key: string]: boolean}) => {
    this.setState({ conceptResults });
  }

  updateResponseConceptResultInMassEditArray = () => {
    const { actions, dispatch, questionID, selectedResponses } = this.props;
    dispatch(actions.submitConceptResults(selectedResponses, this.state.conceptResults, questionID));
  }

  updateResponseFeedbackInMassEditArray = () => {
    const { actions, dispatch, questionID, selectedResponses } = this.props;
    const { massEditFeedback } = this.state;
    const optimal = this.massEditOptimalRef.current?.checked || false;
    const payload = {
      author: null,
      feedback: massEditFeedback,
      optimal,
      parent_id: null,
    };
    dispatch(actions.submitFeedback(selectedResponses, payload, questionID));
  }

  renderMassEditSummaryListResponse = (response: string) => {
    return (
      <p key={response}>
        <label>
          <input
            checked
            onChange={() => this.removeResponseFromMassEditArray(response)}
            style={{ marginRight: '0.5em' }}
            type="checkbox"
          />
          {this.state.responses[response].text}
        </label>
      </p>
    );
  }

  renderMassEditSummaryList = () => {
    const { selectedResponses } = this.props;
    const summaryResponses = selectedResponses.map(response => this.renderMassEditSummaryListResponse(response));
    return (<div className="content">{summaryResponses}</div>);
  }

  renderMassEditForm = () => {
    const { selectedResponses } = this.props;
    const { massEditFeedback, massEditSummaryListButtonText, massEditSummaryListDisplay, selectedMassEditBoilerplate } = this.state;

    return (
      <div>
        <div className="card is-fullwidth has-bottom-margin has-top-margin">
          <header className="card-content expanded">
            <div className="content">
              <h1 className="title is-3" style={{ marginBottom: '0' }}>
                <strong style={{ fontWeight: '700' }}>{selectedResponses.length}</strong> Responses Selected for Mass Editing:
              </h1>
            </div>
          </header>
          <div className="card-content" style={{ display: massEditSummaryListDisplay }}>
            {this.renderMassEditSummaryList()}
          </div>
          <footer className="card-footer">
            <a className="card-footer-item" onClick={this.toggleMassEditSummaryList}>{massEditSummaryListButtonText}</a>
            <a className="card-footer-item" onClick={this.clearResponsesFromMassEditArray}>Deselect All</a>
            <a className="card-footer-item" onClick={this.deleteAllResponsesInMassEditArray}>Delete All</a>
          </footer>
        </div>
        <div className="card is-fullwidth has-bottom-margin has-top-margin">
          <header className="card-content expanded">
            <h1 className="title is-3" style={{ display: 'inline-block' }}>
              Modify Feedback for <strong style={{ fontWeight: '700' }}>{selectedResponses.length}</strong> Responses
            </h1>
          </header>
          <div className="card-content">
            <div className="content">
              <h3>FEEDBACK</h3>
              <TextEditor
                boilerplate={selectedMassEditBoilerplate || ''}
                handleTextChange={this.handleMassEditFeedbackTextChange}
                text={massEditFeedback || ''}
              />
            </div>
            <div className="content">
              <label className="checkbox">
                <h3><input defaultChecked={false} ref={this.massEditOptimalRef} type="checkbox" /> OPTIMAL</h3>
              </label>
            </div>
          </div>
          <footer className="card-footer">
            <a className="card-footer-item" onClick={this.updateResponseFeedbackInMassEditArray}>Update Feedback</a>
          </footer>
        </div>
        <div className="card is-fullwidth has-bottom-margin has-top-margin">
          <header className="card-content expanded">
            <h1 className="title is-3" style={{ display: 'inline-block' }}>
              Add Concept Results for <strong style={{ fontWeight: '700' }}>{selectedResponses.length}</strong> Responses
            </h1>
          </header>
          <div className="card-content">
            <div className="content">
              <h3>
                CONCEPT RESULTS <span style={{ fontSize: '0.7em', marginLeft: '0.75em' }}>All other concept results associated with selected responses will be overwritten</span>
              </h3>
              <ConceptResultList updateConceptResults={this.updateConceptResults} />
            </div>
          </div>
          <footer className="card-footer">
            <a className="card-footer-item" onClick={this.updateResponseConceptResultInMassEditArray}>Add Concept Result</a>
          </footer>
        </div>
      </div>
    );
  }

  render() {
    let content;
    if (Object.keys(this.state.responses).length > 0) {
      content = <div>{this.renderMassEditForm()}</div>;
    } else {
      content = <span>There are no selected responses</span>;
    }
    return content;
  }
}

export { MassEditContainer }
